import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ShoppingBag, 
  TrendingDown, 
  Gift, 
  Shield, 
  ChevronDown,
  ChevronUp,
  Zap,
  Users,
  DollarSign,
  Share2,
  CheckCircle,
  Copy,
  MessageCircle,
  Mail,
  ArrowRight,
  MapPin,
  Globe
} from 'lucide-react';
import FAQ from '../common/FAQ';

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const [currentSaving, setCurrentSaving] = useState(0);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  // ============================================
  // HEADLINE OPTIONS - Change this to test different headlines
  // ============================================
  const headlines = {
    main: "Same Products.",
    highlight: "Way Less Money.",
    
    // Other options to test:
    // main: "Why Pay", highlight: "Retail?"
    // main: "Your Neighbors Stopped", highlight: "Overpaying."
    // main: "Shop Smarter.", highlight: "Save 25-40%."
  };

  // ============================================
  // TIERED PRICING CALCULATOR
  // Based on UniLedge's pricing model:
  // 5+ buyers: 8-10% off
  // 10+ buyers: 16-19% off
  // 25+ buyers: 23-30% off
  // 50+ buyers: 35-40% off
  // ============================================
  const calculateTierPrice = (retailPrice, buyerCount) => {
    let discountPercent = 0;
    let nextTierBuyers = 5;
    let nextTierDiscount = 10;
    
    if (buyerCount >= 50) {
      discountPercent = 38; // 35-40% range
      nextTierBuyers = null; // Max tier
      nextTierDiscount = null;
    } else if (buyerCount >= 25) {
      discountPercent = 27; // 23-30% range
      nextTierBuyers = 50;
      nextTierDiscount = 38;
    } else if (buyerCount >= 10) {
      discountPercent = 18; // 16-19% range
      nextTierBuyers = 25;
      nextTierDiscount = 27;
    } else if (buyerCount >= 5) {
      discountPercent = 9; // 8-10% range
      nextTierBuyers = 10;
      nextTierDiscount = 18;
    } else {
      discountPercent = 0;
      nextTierBuyers = 5;
      nextTierDiscount = 9;
    }
    
    const currentPrice = retailPrice * (1 - discountPercent / 100);
    const nextTierPrice = nextTierDiscount ? retailPrice * (1 - nextTierDiscount / 100) : null;
    const buyersNeeded = nextTierBuyers ? nextTierBuyers - buyerCount : 0;
    
    return {
      currentPrice: Math.round(currentPrice * 100) / 100,
      discountPercent,
      nextTierPrice: nextTierPrice ? Math.round(nextTierPrice * 100) / 100 : null,
      buyersNeeded,
      nextTierBuyers
    };
  };

  // ============================================
  // LIVE SAVINGS TICKER DATA
  // Shows activity across different regions
  // ============================================
  const savingsExamples = [
    { name: "Sarah", zip: "95823", amount: 12.50, item: "laundry supplies" },
    { name: "Marcus", zip: "95816", amount: 8.99, item: "cleaning products" },
    { name: "Jennifer", zip: "94102", amount: 15.75, item: "personal care" },
    { name: "David", zip: "90210", amount: 22.00, item: "household essentials" },
    { name: "Lisa", zip: "95822", amount: 9.50, item: "health products" },
    { name: "Carlos", zip: "93706", amount: 18.25, item: "dish soap" },
    { name: "Amanda", zip: "92101", amount: 11.00, item: "body wash" },
    { name: "Kevin", zip: "95814", amount: 7.99, item: "paper towels" },
  ];

  // ============================================
  // FEATURED PRODUCTS - FROM YOUR ACTUAL DATABASE
  // Real Cloudinary images from your product catalog
  // Strategic distribution across regions to show network growth
  // Pricing calculated using tiered discount model
  // ============================================
  const featuredProducts = [
    // SACRAMENTO (Home Base - Strong Activity)
    {
      id: 1,
      name: "Tide Power PODS with Downy, April Fresh, 104ct",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764462797/adrjogveewiqxbrch8rz.webp",
      fallbackEmoji: "🧺",
      retailPrice: 26.99,
      buyersCount: 47,
      region: "Sacramento",
      category: "Household"
    },
    {
      id: 2,
      name: "Charmin Ultra Soft Toilet Paper 6 Mega Rolls",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764659376/rybv7uh8prnt4rwhct13.webp",
      fallbackEmoji: "🧻",
      retailPrice: 4.99,
      buyersCount: 63,
      region: "Sacramento",
      category: "Household"
    },
    {
      id: 3,
      name: "Dawn Platinum Advanced Power Dish Soap, 90 fl oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764643282/isihereaoxrxfkeyrnnc.webp",
      fallbackEmoji: "🫧",
      retailPrice: 12.99,
      buyersCount: 38,
      region: "Sacramento",
      category: "Household"
    },
    {
      id: 4,
      name: "Dove Moisturizing Liquid Body Wash, 23 oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764731663/ftcb81lnduqrdlrjevkx.webp",
      fallbackEmoji: "🧴",
      retailPrice: 6.49,
      buyersCount: 29,
      region: "Sacramento",
      category: "Health & Wellness"
    },
    // BAY AREA (Growing Market)
    {
      id: 5,
      name: "Clorox Disinfecting Liquid Bleach, 121 fl oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764582424/cf58nuv37hcifbitlkds.webp",
      fallbackEmoji: "🧹",
      retailPrice: 5.99,
      buyersCount: 24,
      region: "Bay Area",
      category: "Household"
    },
    {
      id: 6,
      name: "Aveeno Daily Moisturizing Body Lotion, 24 oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764810492/c876okrtgpy3d820ziaa.webp",
      fallbackEmoji: "🧴",
      retailPrice: 8.99,
      buyersCount: 18,
      region: "Bay Area",
      category: "Health & Wellness"
    },
    {
      id: 7,
      name: "Pompeian Extra Virgin Olive Oil, 68 fl oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764726176/sjpnbxxt1s1x5zffedzz.webp",
      fallbackEmoji: "🫒",
      retailPrice: 16.99,
      buyersCount: 31,
      region: "Bay Area",
      category: "Pantry"
    },
    // STOCKTON/FRESNO (Central Valley - Emerging)
    {
      id: 8,
      name: "GAIN + OXI Liquid Laundry Detergent, 159 oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764462202/raufnlga6bukdzsvbtp0.webp",
      fallbackEmoji: "🧺",
      retailPrice: 18.99,
      buyersCount: 12,
      region: "Stockton",
      category: "Household"
    },
    {
      id: 9,
      name: "Fabuloso Multi-Purpose Cleaner, Lavender, 210 fl oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764579670/o7ymg7fcqocxafspvhnp.webp",
      fallbackEmoji: "✨",
      retailPrice: 9.99,
      buyersCount: 8,
      region: "Fresno",
      category: "Household"
    },
    // LOS ANGELES (SoCal - Just Launched)
    {
      id: 10,
      name: "Neutrogena Makeup Remover Wipes, 25 Count",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764810100/xaj7n3qieeotqfaatsqt.webp",
      fallbackEmoji: "🧖‍♀️",
      retailPrice: 4.49,
      buyersCount: 6,
      region: "Los Angeles",
      category: "Health & Wellness"
    },
    {
      id: 11,
      name: "Bounce Outdoor Fresh Dryer Sheets, 400ct",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764576820/kvmlnl9d9fyo8i4vazti.webp",
      fallbackEmoji: "🌸",
      retailPrice: 11.59,
      buyersCount: 11,
      region: "Los Angeles",
      category: "Household"
    },
    // SAN DIEGO (Brand New Market)
    {
      id: 12,
      name: "Cetaphil Moisturizing Cream, 16 fl oz",
      image: "https://res.cloudinary.com/drcldizc0/image/upload/v1764738949/kpi1d7m3caawmc3atpha.webp",
      fallbackEmoji: "🧴",
      retailPrice: 9.49,
      buyersCount: 4,
      region: "San Diego",
      category: "Health & Wellness"
    },
  ];

  // Get unique regions for display
  const uniqueRegions = [...new Set(featuredProducts.map(p => p.region))];
  
  // Calculate total buyers across all products
  const totalBuyers = featuredProducts.reduce((sum, p) => sum + p.buyersCount, 0);

  // Rotate through savings examples
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSaving(prev => (prev + 1) % savingsExamples.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Rotate through products
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProduct(prev => (prev + 1) % featuredProducts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleStartShopping = () => {
    navigate('/register');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText('https://uniledge.io');
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareSMS = () => {
    const message = "Check out UniLedge - I found a way to save 25-40% on everyday products! https://uniledge.io";
    window.open(`sms:?body=${encodeURIComponent(message)}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = "Save 25-40% on Everyday Products";
    const body = "Hey! I found this cool shopping site called UniLedge where prices drop as more people buy. Check it out: https://uniledge.io";
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const handleShareWhatsApp = () => {
    const message = "Check out UniLedge - Save 25-40% on everyday products! The more people buy, the lower the price. https://uniledge.io";
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Get current product and calculate its pricing
  const product = featuredProducts[currentProduct];
  const pricing = calculateTierPrice(product.retailPrice, product.buyersCount);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex flex-col">
      
      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <div className="pt-10 pb-6 px-6 text-center">
        {/* Logo */}
        <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center text-white font-bold text-lg mb-5 mx-auto shadow-lg">
          UL
        </div>
        
        {/* Main Headline */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3 leading-tight">
          {headlines.main}<br />
          <span className="text-green-600">{headlines.highlight}</span>
        </h1>
        
        {/* Subheadline */}
        <p className="text-gray-600 text-base mb-1">
          Shop smart. Save 25-40% on everyday items.
        </p>
        <p className="text-gray-500 text-sm">
          The more people buy, the lower the price goes.
        </p>
      </div>

      {/* ============================================ */}
      {/* LIVE SAVINGS TICKER */}
      {/* ============================================ */}
      <div className="px-6 mb-5">
        <div className="bg-green-50 border border-green-100 rounded-xl p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-3"></div>
              <div>
                <p className="text-sm text-green-800 font-medium">
                  {savingsExamples[currentSaving].name} in {savingsExamples[currentSaving].zip}
                </p>
                <p className="text-xs text-green-600">
                  just saved ${savingsExamples[currentSaving].amount.toFixed(2)} on {savingsExamples[currentSaving].item}
                </p>
              </div>
            </div>
            <TrendingDown size={18} className="text-green-600" />
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* FEATURED PRODUCT - REAL PRODUCTS WITH TIERED PRICING */}
      {/* ============================================ */}
      <div className="px-6 mb-5">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          {/* Header with Network Stats */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center">
              <Globe size={14} className="text-blue-500 mr-1.5" />
              <span className="text-xs font-medium text-gray-600">Deals Across Our Network</span>
            </div>
            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-medium">
              {pricing.discountPercent}% OFF
            </span>
          </div>
          
          {/* Network Stats Bar */}
          <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pb-2 border-b border-gray-100">
            <span>{uniqueRegions.length} regions</span>
            <span>•</span>
            <span>{featuredProducts.length}+ deals</span>
            <span>•</span>
            <span>{totalBuyers}+ people buying</span>
          </div>
          
          <div className="flex items-center">
            {/* Product Image - Shows emoji for now, real images coming soon */}
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden mr-4 flex-shrink-0">
              {(!product.image || imageErrors[product.id]) ? (
                <span className="text-3xl">{product.fallbackEmoji}</span>
              ) : (
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={() => setImageErrors(prev => ({ ...prev, [product.id]: true }))}
                />
              )}
            </div>
            
            {/* Product Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
              </div>
              
              {/* Region + Category Badge */}
              <div className="flex items-center gap-2 mt-0.5 mb-1">
                <div className="flex items-center">
                  <MapPin size={12} className="text-blue-500 mr-1" />
                  <span className="text-xs text-blue-600 font-medium">{product.region}</span>
                </div>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {product.category}
                </span>
              </div>
              
              {/* Pricing */}
              <div className="flex items-center">
                <span className="text-lg font-bold text-green-600">${pricing.currentPrice.toFixed(2)}</span>
                <span className="text-sm text-gray-400 line-through ml-2">${product.retailPrice.toFixed(2)}</span>
              </div>
              
              {/* Buyers Progress + Next Tier */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>{product.buyersCount} people buying</span>
                  {pricing.nextTierPrice && (
                    <span className="text-blue-600 font-medium">
                      ${pricing.nextTierPrice.toFixed(2)} with {pricing.buyersNeeded} more!
                    </span>
                  )}
                  {!pricing.nextTierPrice && (
                    <span className="text-green-600 font-medium">🔥 Max savings unlocked!</span>
                  )}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ 
                      width: pricing.nextTierBuyers 
                        ? `${(product.buyersCount / pricing.nextTierBuyers) * 100}%`
                        : '100%'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Regional Disclaimer + Coming Soon */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              🌎 Product availability varies by region
            </p>
            <p className="text-xs text-blue-600 font-semibold text-center mt-1">
              🚀 More products coming soon!
            </p>
            <button 
              onClick={handleStartShopping}
              className="w-full mt-2 text-sm text-blue-600 font-medium flex items-center justify-center hover:text-blue-700"
            >
              See what's available in your area
              <ArrowRight size={14} className="ml-1" />
            </button>
          </div>
          
          {/* Product navigation dots */}
          <div className="flex justify-center items-center space-x-1 mt-3">
            {featuredProducts.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentProduct(index)}
                className={`rounded-full transition-all ${
                  index === currentProduct 
                    ? 'bg-blue-600 w-2.5 h-2.5' 
                    : 'bg-gray-300 w-1.5 h-1.5 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* HOW IT WORKS - ALWAYS EXPANDED */}
      {/* ============================================ */}
      <div className="px-6 mb-5">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            How It Works
          </h2>
          
          <div className="space-y-4">
            {/* Step 1 */}
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <ShoppingBag size={16} className="text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">1. Browse & Add to Cart</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Find everyday essentials & products you need. Prices are already discounted below retail. 
                  You'll see how many people are buying and when prices might drop even more. No membership fees.
                </p>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="flex items-start">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <DollarSign size={16} className="text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">2. Checkout Securely</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pay with card or use your earned tokens. Your payment is protected until delivery. 
                  Our tech groups nearby buyers together. More buyers = bigger discounts.
                  If more people buy before the sale ends, everyone's price drops automatically!
                </p>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="flex items-start">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                <Gift size={16} className="text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">3. Get Your Order & Earn</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Receive your products and earn tokens with every purchase. 
                  Share with friends to earn more tokens and help everyone save more!
                </p>
              </div>
            </div>
          </div>
          
          {/* Price Drop Explainer */}
          <div className="bg-blue-50 rounded-lg p-3 mt-4">
            <p className="text-xs text-blue-800">
              <span className="font-semibold">💡 How prices drop:</span> When more people buy the same product, 
              we negotiate better bulk rates with vendors. Those savings go directly to you - 
              everyone who bought gets the lower price!
            </p>
          </div>
      
      


          
          {/* Learn More Link */}
          <div className="px-5 pb-4">
            <Link 
              to="/transparency" 
              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              See our transparency report
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        
        {/* FAQ Section */}
        <div className="mt-6">
          <FAQ maxItems={3} compact={true} />
        </div>
      </div>


      {/* ============================================ */}
      {/* STATS BAR */}
      {/* ============================================ */}
      <div className="px-6 mb-5">
        <div className="bg-blue-50 rounded-xl p-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-xl font-bold text-blue-600">25-40%</div>
              <div className="text-xs text-gray-600">Avg. Savings</div>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-600">{uniqueRegions.length}</div>
              <div className="text-xs text-gray-600">Regions</div>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-600">$0</div>
              <div className="text-xs text-gray-600">Membership</div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SHARE SECTION - PROMINENT & FULLY CLICKABLE */}
      {/* ============================================ */}
      <div className="px-6 mb-5">
        <div 
          onClick={() => setShowShareOptions(!showShareOptions)}
          className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 border-2 border-purple-200 cursor-pointer hover:border-purple-300 transition-all"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
                <Share2 size={20} className="text-white" />
              </div>
              <div>
                <span className="font-bold text-gray-900 text-base">Share & Earn</span>
                <p className="text-xs text-gray-600">Tap to share with friends</p>
              </div>
            </div>
            <div className="bg-white rounded-full p-2 shadow-sm">
              {showShareOptions ? (
                <ChevronUp size={18} className="text-purple-600" />
              ) : (
                <ChevronDown size={18} className="text-purple-600" />
              )}
            </div>
          </div>
          
          {/* Benefits */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="flex items-center text-xs bg-white/60 rounded-lg px-2 py-1.5">
              <CheckCircle size={14} className="text-green-500 mr-1.5 flex-shrink-0" />
              <span className="text-gray-700">Earn <span className="font-bold">25 tokens</span></span>
            </div>
            <div className="flex items-center text-xs bg-white/60 rounded-lg px-2 py-1.5">
              <CheckCircle size={14} className="text-green-500 mr-1.5 flex-shrink-0" />
              <span className="text-gray-700">Friends get <span className="font-bold">$2 off</span></span>
            </div>
          </div>
          
          {/* Share Options */}
          {showShareOptions && (
            <div className="mt-4 pt-4 border-t border-purple-200" onClick={(e) => e.stopPropagation()}>
              <p className="text-xs text-gray-700 mb-2 text-center font-medium">
                📦 Share your link so UniLedge can offer MORE of what YOU need!
              </p>
              <p className="text-xs text-gray-500 mb-3 text-center">
                Sign up to get your personal referral link and track your earnings.
              </p>
              
              <div className="grid grid-cols-4 gap-2">
                <button 
                  onClick={handleCopyLink}
                  className="flex flex-col items-center p-3 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                >
                  <Copy size={22} className={linkCopied ? "text-green-500" : "text-gray-600"} />
                  <span className="text-xs mt-1.5 font-medium text-gray-700">
                    {linkCopied ? "Copied!" : "Copy"}
                  </span>
                </button>
                
                <button 
                  onClick={handleShareSMS}
                  className="flex flex-col items-center p-3 bg-white rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all"
                >
                  <MessageCircle size={22} className="text-green-600" />
                  <span className="text-xs mt-1.5 font-medium text-gray-700">Text</span>
                </button>
                
                <button 
                  onClick={handleShareWhatsApp}
                  className="flex flex-col items-center p-3 bg-white rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all"
                >
                  <MessageCircle size={22} className="text-green-500" />
                  <span className="text-xs mt-1.5 font-medium text-gray-700">WhatsApp</span>
                </button>
                
                <button 
                  onClick={handleShareEmail}
                  className="flex flex-col items-center p-3 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                >
                  <Mail size={22} className="text-blue-600" />
                  <span className="text-xs mt-1.5 font-medium text-gray-700">Email</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* TRUST ELEMENTS */}
      {/* ============================================ */}
      <div className="px-6 mb-6">
        <div className="flex justify-center space-x-5">
          <div className="flex items-center text-gray-500 text-xs">
            <Shield size={14} className="mr-1" />
            <span>Secure Checkout</span>
          </div>
          <div className="flex items-center text-gray-500 text-xs">
            <Zap size={14} className="mr-1" />
            <span>Instant Savings</span>
          </div>
          <div className="flex items-center text-gray-500 text-xs">
            <Users size={14} className="mr-1" />
            <span>Real Vendors</span>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* CTA BUTTONS */}
      {/* ============================================ */}
      <div className="mt-auto px-6 pb-6">
        <button 
          onClick={handleStartShopping}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3.5 rounded-xl font-semibold mb-3 hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg flex items-center justify-center"
        >
          Start Shopping
          <ArrowRight size={18} className="ml-2" />
        </button>
        
        <button 
          onClick={handleSignIn}
          className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3.5 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
        >
          Already have an account? <span className="text-blue-600">Sign In</span>
        </button>
      </div>

      {/* ============================================ */}
      {/* BOTTOM TEXT & LEGAL LINKS */}
      {/* ============================================ */}
      <div className="text-center pb-6 px-6">
        <p className="text-xs text-gray-400 mb-2">
          No membership fees. No commitment. Just savings.
        </p>
        <div className="flex justify-center items-center space-x-2 text-xs">
          <Link to="/privacy" className="text-gray-500 hover:text-blue-600 hover:underline transition-colors">
            Privacy Policy
          </Link>
          <span className="text-gray-300">|</span>
          <Link to="/terms" className="text-gray-500 hover:text-blue-600 hover:underline transition-colors">
            Terms of Service
          </Link>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          © {new Date().getFullYear()} UniLedge LLC
        </p>
      </div>
    </div>
  );
};

export default WelcomeScreen;